import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { Toaster } from '@/components/ui/sonner';
import Layout from '@/components/Layout';
import LoginPage from '@/pages/LoginPage';
import CommandPage from '@/pages/CommandPage';
import StrategistHubPage from '@/pages/StrategistHubPage';
import AnalyticsPage from '@/pages/AnalyticsPage';
import ContactsPage from '@/pages/ContactsPage';
import EventsPage from '@/pages/EventsPage';
import ProductsPage from '@/pages/ProductsPage';
import SalesPage from '@/pages/SalesPage';
import ApprovalQueuePage from '@/pages/ApprovalQueuePage';
import OrchestraPage from '@/pages/OrchestraPage';
import ArchivePage from '@/pages/ArchivePage';
import CompanyPage from '@/pages/CompanyPage';
import TesterPage from '@/pages/TesterPage';
import ActivityLogPage from '@/pages/ActivityLogPage';
import DashboardPage from '@/pages/DashboardPage';
import StrategicDashboardPage from '@/pages/StrategicDashboardPage';
import OperationsPage from '@/pages/OperationsPage';
import PhasePage from '@/pages/PhasePage';
import EvolutionPage from '@/pages/EvolutionPage';
import StrategyAdvancedPage from '@/pages/StrategyAdvancedPage';
import FunnelPage from '@/pages/FunnelPage';
import AdsMonitorPage from '@/pages/AdsMonitorPage';
import MetaPage from '@/pages/MetaPage';
import HealthPage from '@/pages/HealthPage';
import AgendaPage from '@/pages/AgendaPage';
import FoundationPage from '@/pages/FoundationPage';
import OnboardingPage from '@/pages/OnboardingPage';
import SettingsPage from '@/pages/SettingsPage';
import OrientationPage from '@/pages/OrientationPage';
import AssetsPage from '@/pages/AssetsPage';
import ContentsPage from '@/pages/ContentsPage';
import CampaignsPage from '@/pages/CampaignsPage';
import NetworkPage from '@/pages/NetworkPage';
import TagManagerPage from '@/pages/TagManagerPage';
import PulseMapPage from '@/pages/PulseMapPage';
import CentroComandoPage from '@/pages/CentroComandoPage';
import PublishingPage from '@/pages/PublishingPage';
import SocialProfilesPage from '@/pages/SocialProfilesPage';
import TaskRegistryPage from '@/pages/TaskRegistryPage';
import BoardPage from '@/pages/BoardPage';
import HomePage from '@/pages/HomePage';
import TimelinePage from '@/pages/TimelinePage';
import PianoEditorialePage from '@/pages/PianoEditorialePage';
import AgentsPage from '@/pages/AgentsPage';
import WorkspacePage from '@/pages/WorkspacePage';
import ContentStudioPage from '@/pages/ContentStudioPage';
import DeepResearchPage from '@/pages/DeepResearchPage';
import PlannerPage from '@/pages/PlannerPage';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-4 border-[#2E75B6] border-t-transparent rounded-full animate-spin" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />

      {/* Main navigation (sidebar) */}
      <Route path="/home" element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
      <Route path="/command" element={<ProtectedRoute><CentroComandoPage /></ProtectedRoute>} />
      <Route path="/board" element={<ProtectedRoute><BoardPage /></ProtectedRoute>} />
      <Route path="/board/:boardId" element={<ProtectedRoute><BoardPage /></ProtectedRoute>} />
      <Route path="/timeline" element={<ProtectedRoute><TimelinePage /></ProtectedRoute>} />
      <Route path="/editorial" element={<Navigate to="/planner" replace />} />
      <Route path="/workspace" element={<ProtectedRoute><WorkspacePage /></ProtectedRoute>} />
      <Route path="/pulse" element={<ProtectedRoute><PulseMapPage /></ProtectedRoute>} />
      <Route path="/agenda" element={<ProtectedRoute><AgendaPage /></ProtectedRoute>} />
      <Route path="/strategist" element={<Navigate to="/command?panel=strategist" replace />} />
      <Route path="/contents" element={<Navigate to="/content-studio" replace />} />
      <Route path="/content-studio" element={<ProtectedRoute><ContentStudioPage /></ProtectedRoute>} />
      <Route path="/research" element={<ProtectedRoute><DeepResearchPage /></ProtectedRoute>} />
      <Route path="/planner" element={<ProtectedRoute><PlannerPage /></ProtectedRoute>} />
      <Route path="/approval" element={<ProtectedRoute><ApprovalQueuePage /></ProtectedRoute>} />
      <Route path="/campaigns" element={<ProtectedRoute><CampaignsPage /></ProtectedRoute>} />
      <Route path="/publishing" element={<ProtectedRoute><PublishingPage /></ProtectedRoute>} />
      <Route path="/social-profiles" element={<ProtectedRoute><SocialProfilesPage /></ProtectedRoute>} />
      <Route path="/task-registry" element={<ProtectedRoute><TaskRegistryPage /></ProtectedRoute>} />
      <Route path="/events" element={<ProtectedRoute><EventsPage /></ProtectedRoute>} />
      <Route path="/contacts" element={<ProtectedRoute><ContactsPage /></ProtectedRoute>} />
      <Route path="/sales" element={<ProtectedRoute><SalesPage /></ProtectedRoute>} />
      <Route path="/products" element={<ProtectedRoute><ProductsPage /></ProtectedRoute>} />
      <Route path="/network" element={<ProtectedRoute><NetworkPage /></ProtectedRoute>} />
      <Route path="/company" element={<ProtectedRoute><CompanyPage /></ProtectedRoute>} />
      <Route path="/agents" element={<ProtectedRoute><AgentsPage /></ProtectedRoute>} />
      <Route path="/tester" element={<ProtectedRoute><TesterPage /></ProtectedRoute>} />
      <Route path="/tags" element={<ProtectedRoute><TagManagerPage /></ProtectedRoute>} />

      {/* Redirects from old routes */}
      <Route path="/archive" element={<Navigate to="/contents" replace />} />
      <Route path="/assets" element={<Navigate to="/contents" replace />} />
      <Route path="/funnel" element={<Navigate to="/sales" replace />} />
      <Route path="/activity" element={<Navigate to="/agents" replace />} />
      <Route path="/orchestra" element={<Navigate to="/agents" replace />} />
      <Route path="/agents-hub" element={<Navigate to="/agents" replace />} />
      <Route path="/strategist" element={<Navigate to="/command" replace />} />

      {/* Secondary pages (accessible but not in sidebar) */}
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/home" replace />} />
      <Route path="*" element={<Navigate to="/home" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="bottom-right" richColors toastOptions={{ duration: 4000 }} />
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
